import { defineStore } from 'pinia';
import { api } from '@/api/client';
import type { EventItem, EventCreateResponse, EventCompleteResponse } from '@/api/types';
import { useAwardsStore } from '@/stores/awards';

interface EventsState {
  upcoming: EventItem[];
  byRange: Record<string, EventItem[]>;
}

export const useEventsStore = defineStore('events', {
  state: (): EventsState => ({ upcoming: [], byRange: {} }),
  actions: {
    async loadUpcoming(limit = 10) {
      this.upcoming = await api<EventItem[]>('GET', `/api/events/upcoming?limit=${limit}`);
    },
    async loadRange(from: string, to: string) {
      const key = `${from}..${to}`;
      this.byRange[key] = await api<EventItem[]>('GET', `/api/events?from=${from}&to=${to}`);
      return this.byRange[key];
    },
    async create(payload: {
      categoryId: string;
      title: string;
      description?: string;
      date: string;
      startTime: string;
      endTime: string;
      recurrence?: { freq: string; endDate: string };
    }) {
      const resp = await api<EventCreateResponse>('POST', '/api/events', payload);
      this.upcoming.unshift(resp.event);
      useAwardsStore().enqueue(resp.awardsGranted);
      // A repeating series materialises many events across the calendar —
      // refresh every loaded range/list so they all appear at once.
      if ((resp.occurrencesCreated ?? 1) > 1) await this.reloadAll();
      return resp;
    },
    async update(
      id: string,
      patch: Partial<{ categoryId: string; title: string; description: string | null; date: string; startTime: string; endTime: string }>,
      scope: 'this' | 'following' | 'all' = 'this',
    ) {
      const updated = await api<EventItem>('PUT', `/api/events/${id}`, { ...patch, scope });
      this.replaceLocal(updated);
      // "following"/"all" edits touch sibling occurrences too — re-pull them.
      if (scope !== 'this') await this.reloadAll();
      return updated;
    },
    async complete(id: string) {
      const resp = await api<EventCompleteResponse>('POST', `/api/events/${id}/complete`);
      this.replaceLocal(resp.event);
      useAwardsStore().enqueue(resp.awardsGranted);
      return resp.event;
    },
    async remove(id: string, scope: 'this' | 'following' | 'all' = 'this') {
      const qs = scope !== 'this' ? `?scope=${scope}` : '';
      await api('DELETE', `/api/events/${id}${qs}`);
      this.upcoming = this.upcoming.filter((e) => e.id !== id);
      for (const k of Object.keys(this.byRange)) {
        this.byRange[k] = this.byRange[k].filter((e) => e.id !== id);
      }
      // A series-wide delete removed sibling occurrences server-side too.
      if (scope !== 'this') await this.reloadAll();
    },
    /** Re-pull the upcoming list and every loaded week range. Used after a
     *  series-wide create/edit/delete that changed many events at once. */
    async reloadAll() {
      const ranges = Object.keys(this.byRange);
      await Promise.all([
        this.loadUpcoming(Math.max(this.upcoming.length, 20)),
        ...ranges.map((k) => {
          const [from, to] = k.split('..');
          return this.loadRange(from, to);
        }),
      ]);
    },
    replaceLocal(updated: EventItem) {
      const ix = this.upcoming.findIndex((e) => e.id === updated.id);
      if (ix >= 0) this.upcoming[ix] = updated;
      for (const k of Object.keys(this.byRange)) {
        const j = this.byRange[k].findIndex((e) => e.id === updated.id);
        if (j >= 0) this.byRange[k][j] = updated;
      }
    },
  },
});
